import React from 'react';

interface TypographyProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption' | 'overline' | 'display1' | 'display2';
  color?: 'primary' | 'secondary' | 'muted' | 'subtle' | 'accent' | 'error' | 'success' | 'warning' | 'high-contrast';
  weight?: 'thin' | 'extralight' | 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black';
  align?: 'left' | 'center' | 'right' | 'justify';
  truncate?: boolean;
  gradient?: boolean;
  responsive?: boolean;
  reading?: boolean;
  id?: string;
}

export function Typography({ 
  children, 
  className = '', 
  variant = 'body1',
  color = 'primary',
  weight,
  align = 'left',
  truncate = false,
  gradient = false,
  responsive = false,
  reading = false,
  id
}: TypographyProps) {
  const baseClasses = {
    // Display variants for hero content
    display1: responsive 
      ? 'text-fluid-3xl font-outfit font-black text-heading'
      : 'text-6xl md:text-7xl font-outfit font-black text-heading',
    display2: responsive 
      ? 'text-fluid-2xl font-outfit font-extrabold text-heading'
      : 'text-5xl md:text-6xl font-outfit font-extrabold text-heading',
    // Standard heading hierarchy
    h1: responsive 
      ? 'text-fluid-2xl font-outfit font-bold text-heading'
      : 'text-4xl md:text-5xl font-outfit font-bold text-heading',
    h2: responsive 
      ? 'text-fluid-xl font-outfit font-bold text-heading'
      : 'text-3xl md:text-4xl font-outfit font-bold text-heading',
    h3: responsive 
      ? 'text-fluid-lg font-outfit font-semibold text-heading'
      : 'text-2xl md:text-3xl font-outfit font-semibold text-heading',
    h4: responsive 
      ? 'text-fluid-base font-outfit font-semibold text-heading'
      : 'text-xl md:text-2xl font-outfit font-semibold text-heading',
    h5: responsive 
      ? 'text-fluid-sm font-outfit font-medium text-heading'
      : 'text-lg md:text-xl font-outfit font-medium text-heading',
    h6: responsive 
      ? 'text-fluid-sm font-outfit font-medium text-heading'
      : 'text-base md:text-lg font-outfit font-medium text-heading',
    // Subtitles with better hierarchy
    subtitle1: `text-lg font-inter font-medium ${reading ? 'text-reading' : 'leading-relaxed'}`,
    subtitle2: `text-base font-inter font-medium ${reading ? 'text-reading' : 'leading-relaxed'}`,
    // Body text optimized for reading
    body1: `text-base font-inter font-normal ${reading ? 'text-reading' : 'leading-relaxed'}`,
    body2: `text-sm font-inter font-normal ${reading ? 'text-reading' : 'leading-relaxed'}`,
    // Utility text styles
    caption: 'text-xs font-inter font-normal leading-normal tracking-wide',
    overline: 'text-xs font-inter font-semibold uppercase tracking-widest leading-normal'
  };

  const colorClasses = {
    primary: 'text-text-primary',
    secondary: 'text-text-secondary',
    muted: 'text-text-muted',
    subtle: 'text-text-subtle',
    accent: 'text-naia-pink',
    error: 'text-error',
    success: 'text-success',
    warning: 'text-warning',
    'high-contrast': 'text-high-contrast-text'
  };

  const weightClasses = {
    thin: 'font-thin',
    extralight: 'font-extralight',
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    extrabold: 'font-extrabold',
    black: 'font-black'
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify'
  };

  const gradientClass = gradient ? 'bg-gradient-to-r from-naia-pink to-cyan-accent bg-clip-text text-transparent' : '';
  const truncateClass = truncate ? 'truncate' : '';
  const weightClass = weight ? weightClasses[weight] : '';

  const Tag = variant.startsWith('h') || variant.startsWith('display') ? variant.replace('display', 'h') as keyof JSX.IntrinsicElements : 'span';

  return (
    <Tag 
      className={`
        ${baseClasses[variant]}
        ${gradientClass || colorClasses[color]}
        ${weightClass}
        ${alignClasses[align]}
        ${truncateClass}
        ${reading ? 'text-balance' : ''}
        ${className}
      `.trim()}
      id={id}
    >
      {children}
    </Tag>
  );
}

// Enhanced section header with better accessibility
interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  decoration?: 'line' | 'glow' | 'dots' | 'none';
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  responsive?: boolean;
}

export function SectionHeader({ 
  title, 
  subtitle, 
  icon, 
  action, 
  decoration = 'line',
  level = 2,
  responsive = true
}: SectionHeaderProps) {
  const decorationElements = {
    line: (
      <div className="absolute bottom-0 left-0 w-24 h-px bg-gradient-to-r from-naia-pink to-transparent"></div>
    ),
    glow: (
      <div className="absolute -bottom-2 left-0 w-16 h-0.5 bg-gradient-to-r from-naia-pink to-cyan-accent rounded-full blur-sm"></div>
    ),
    dots: (
      <div className="absolute -bottom-1 left-0 flex gap-1">
        <div className="w-2 h-2 bg-naia-pink rounded-full"></div>
        <div className="w-1.5 h-1.5 bg-cyan-accent rounded-full"></div>
        <div className="w-1 h-1 bg-soft-violet rounded-full"></div>
      </div>
    ),
    none: null
  };

  const headingVariant = `h${level}` as any;

  return (
    <header className="flex items-center justify-between mb-8" role="banner">
      <div className="relative">
        <div className="flex items-center gap-3">
          {icon && (
            <span className="text-naia-pink text-xl flex-shrink-0" aria-hidden="true">
              {icon}
            </span>
          )}
          <Typography 
            variant={headingVariant} 
            className="relative" 
            responsive={responsive}
            id={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {title}
          </Typography>
        </div>
        {subtitle && (
          <Typography 
            variant="subtitle2" 
            color="secondary" 
            className="mt-2" 
            reading
            responsive={responsive}
          >
            {subtitle}
          </Typography>
        )}
        {decoration !== 'none' && decorationElements[decoration]}
      </div>
      {action && (
        <div className="flex-shrink-0">
          {action}
        </div>
      )}
    </header>
  );
}

// Reading-optimized text component for longer content
interface ReadingTextProps {
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'base' | 'lg';
  maxWidth?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

export function ReadingText({ 
  children, 
  className = '', 
  size = 'base',
  maxWidth = 'lg'
}: ReadingTextProps) {
  const sizeClasses = {
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg'
  };

  const maxWidthClasses = {
    none: '',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl'
  };

  return (
    <div className={`
      ${sizeClasses[size]}
      ${maxWidthClasses[maxWidth]}
      font-inter text-reading text-balance
      ${className}
    `.trim()}>
      {children}
    </div>
  );
} 